import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { teamAPI } from '../api';

// ─── Static tier config ────────────────────────────────────────────────────────

const TIERS = [
  { id: 'founder',    label: 'Founder',               cardPx: 260 },
  { id: 'ownership',  label: 'Ownership',              cardPx: 220 },
  { id: 'management', label: 'Management Team',        cardPx: 175 },
  { id: 'operations', label: 'Operations Management',  cardPx: 190 },
  { id: 'senior',     label: 'Senior Operators',       cardPx: 165 },
];

const MORE = [
  { label: 'Mechanics',   desc: 'Keeping every rig road-ready around the clock.' },
  { label: 'Drivers',     desc: 'The backbone of every mile and every delivery.' },
  { label: 'Investors',   desc: 'The fuel that powers our long-term vision.' },
  { label: 'Consultants', desc: 'Expert guidance on compliance, safety and growth.' },
];

// ─── Animation ────────────────────────────────────────────────────────────────

const fadeUp = (delay = 0) => ({
  initial:    { opacity: 0, y: 20 },
  animate:    { opacity: 1, y: 0 },
  transition: { duration: 0.45, delay, ease: [0.22, 1, 0.36, 1] },
});

// ─── Flip Card ────────────────────────────────────────────────────────────────

function MemberCard({ member, delay = 0, cardPx = 180, tierLabel = '' }) {
  const [flipped, setFlipped] = useState(false);
  const photoH = Math.round(cardPx * (4 / 3));
  const cardH  = photoH + 64;

  const name   = member?.name   || 'First Name Last Name';
  const slogan = member?.slogan || '';

  return (
    <motion.div
      {...fadeUp(delay)}
      className="flex-shrink-0 cursor-pointer"
      style={{ width: cardPx, height: cardH, perspective: 900 }}
      onMouseEnter={() => setFlipped(true)}
      onMouseLeave={() => setFlipped(false)}
    >
      <motion.div
        style={{ width: '100%', height: '100%', position: 'relative', transformStyle: 'preserve-3d' }}
        animate={{ rotateY: flipped ? 180 : 0 }}
        transition={{ duration: 0.48, ease: [0.22, 1, 0.36, 1] }}
      >

        {/* ── FRONT ── */}
        <div
          className="brutalist-card absolute inset-0 flex flex-col overflow-hidden"
          style={{ backfaceVisibility: 'hidden' }}
        >
          <div className="h-[3px] w-full bg-purple-600 flex-shrink-0" />
          <div className="flex-1 bg-[#0f0f12] flex items-end justify-center overflow-hidden relative">
            {member?.photo_url ? (
              <img
                src={member.photo_url}
                alt={name}
                className="absolute inset-0 w-full h-full object-cover object-top"
                style={{ filter: 'grayscale(15%)' }}
              />
            ) : (
              <svg viewBox="0 0 100 115" fill="none" xmlns="http://www.w3.org/2000/svg"
                style={{ width: '58%', filter: 'grayscale(100%) contrast(1.1)', opacity: 0.16 }}>
                <circle cx="50" cy="33" r="22" fill="white" />
                <path d="M0 115 C0 78 18 60 50 60 C82 60 100 78 100 115Z" fill="white" />
              </svg>
            )}
          </div>
          <div className="p-3 flex-shrink-0 border-t-2 border-[#27272a]">
            <p className="text-white uppercase tracking-tight leading-tight"
              style={{ fontFamily: "'Abolition', sans-serif", fontSize: 13 }}>
              {name}
            </p>
          </div>
        </div>

        {/* ── BACK ── */}
        <div
          className="brutalist-card absolute inset-0 flex flex-col items-center justify-center overflow-hidden bg-[#0d0d10] px-4 text-center"
          style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
        >
          <div className="h-[3px] w-full bg-purple-600 absolute top-0 left-0" />

          <img src="/logo.png" alt=""
            className="mb-4"
            style={{ width: '36%', opacity: 0.08, filter: 'grayscale(100%)' }}
          />

          {slogan ? (
            <p className="text-[#a1a1aa] text-xs leading-relaxed italic"
              style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              "{slogan}"
            </p>
          ) : (
            <p className="text-[#3f3f46] text-[10px] italic"
              style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              — no slogan set —
            </p>
          )}

          <div className="h-[2px] bg-purple-600 mt-4" style={{ width: 28 }} />

          <span className="text-[#52525b] text-[9px] tracking-widest uppercase mt-3"
            style={{ fontFamily: "'Abolition', sans-serif", letterSpacing: '0.18em' }}>
            {tierLabel}
          </span>
        </div>

      </motion.div>
    </motion.div>
  );
}

// ─── Section divider ──────────────────────────────────────────────────────────

function TierDivider({ label, delay = 0 }) {
  return (
    <motion.div {...fadeUp(delay)} className="flex items-center gap-5 mb-10">
      <div className="w-10 h-[2px] bg-purple-600 flex-shrink-0" />
      <h2 className="text-white uppercase whitespace-nowrap"
        style={{ fontFamily: "'Abolition', sans-serif", fontSize: 22, letterSpacing: '0.18em' }}>
        {label}
      </h2>
      <div className="flex-1 h-px bg-[#27272a]" />
    </motion.div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const Team = () => {
  const [teamData, setTeamData] = useState({});
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    teamAPI.getTeam()
      .then(r => setTeamData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen pt-24 pb-0 bg-[#0a0a0c]">
      <div className="px-6 md:px-12 max-w-7xl mx-auto">

        {/* ── Hero ─────────────────────────────────────────── */}
        <motion.div {...fadeUp(0)} className="mb-20">
          <div className="flex items-center gap-5 mb-5">
            <img src="/logo.png" alt="GTY" className="h-12 md:h-16 w-auto flex-shrink-0"
              style={{ filter: 'grayscale(20%)' }} />
            <div className="w-px h-10 bg-[#27272a]" />
            <span className="overline">Our People</span>
          </div>
          <h1 className="text-5xl md:text-7xl lg:text-8xl tracking-tighter uppercase leading-[0.9] text-white mb-5"
            style={{ fontFamily: "'Abolition', sans-serif" }}>
            Meet<br />The Crew
          </h1>
          <p className="text-[#52525b] text-sm max-w-sm leading-relaxed font-mono">
            The people behind every mile, every decision, every truck on the road.
          </p>
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 border-2 border-[#27272a] divide-x-2 divide-y-2 sm:divide-y-0 divide-[#27272a] max-w-xl">
            {[
              { num: '1',  label: 'Founder' },
              { num: '12', label: 'Management' },
              { num: '3',  label: 'Operations' },
              { num: '4',  label: 'Sr. Operators' },
            ].map(s => (
              <div key={s.label} className="p-4">
                <div className="text-3xl text-white leading-none mb-1"
                  style={{ fontFamily: "'Abolition', sans-serif" }}>{s.num}</div>
                <div className="text-[#52525b] text-[9px] uppercase tracking-widest font-mono">{s.label}</div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* ── Hover hint ────────────────────────────────────── */}
        <motion.p {...fadeUp(0.1)} className="text-[#3f3f46] text-[10px] font-mono tracking-widest mb-10">
          — hover a card to see their motto
        </motion.p>

        {/* ── Tier Sections ─────────────────────────────────── */}
        {!loading && TIERS.map(tier => {
          const members = teamData[tier.id] || [];
          return (
            <section key={tier.id} className="mb-16 pb-16 border-b-2 border-[#1a1a1d]">
              <TierDivider label={tier.label} />
              <div className="flex flex-wrap justify-center gap-3">
                {members.map((m, i) => (
                  <MemberCard
                    key={m.id}
                    member={m}
                    delay={0.04 + i * 0.035}
                    cardPx={tier.cardPx}
                    tierLabel={tier.label}
                  />
                ))}
              </div>
            </section>
          );
        })}

        {/* ── And Many More ─────────────────────────────────── */}
        <section className="pb-24">
          <TierDivider label="And Many More" />
          <p className="text-[#52525b] text-xs leading-relaxed font-mono mb-8 max-w-sm">
            Behind every truck on the road is a wider network of people keeping it all moving.
          </p>
          <div className="flex flex-wrap gap-4">
            {MORE.map((cat, i) => (
              <motion.div key={cat.label} {...fadeUp(i * 0.07)}
                className="brutalist-card p-5" style={{ width: 190 }}>
                <h3 className="text-purple-500 uppercase tracking-widest text-xs mb-2"
                  style={{ fontFamily: "'Abolition', sans-serif", letterSpacing: '0.2em' }}>
                  {cat.label}
                </h3>
                <p className="text-[#52525b] text-xs leading-relaxed font-mono">{cat.desc}</p>
              </motion.div>
            ))}
          </div>
          <motion.p {...fadeUp(0.3)} className="text-[#27272a] tracking-widest mt-12 text-[10px] font-mono">
            — Every person plays a role in keeping GTY moving.
          </motion.p>
        </section>

      </div>
    </div>
  );
};

export default Team;
